import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SubscriptionInfo } from '@app/models/subscription-info';
import { BehaviorSubject, Observable, tap, catchError, of, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SecureStorageService } from './secure-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private _info$ = new BehaviorSubject<SubscriptionInfo | null>(null);
  public readonly info$ = this._info$.asObservable();
  private readonly BASE_URL: string;
  private API_VERSION = 'v1';
  private paymentSuccessSubject = new ReplaySubject<boolean>(1);
  paymentSuccess$ = this.paymentSuccessSubject.asObservable();

  private paymentFailureSubject = new ReplaySubject<boolean>(1);
  paymentFailure$ = this.paymentFailureSubject.asObservable();

  private boosterPackPaymentSuccessSubject = new ReplaySubject<boolean>(1);
  boosterPackPaymentSuccess$ = this.boosterPackPaymentSuccessSubject.asObservable();

  private _isLoaded = false;

  constructor(
    private http: HttpClient,
    private secureStorage: SecureStorageService
  ) {
    this.BASE_URL = environment['apiUrls'].baseUrl;
  }

  async initializeSubscription(): Promise<void> {
    try {
      const currentUser = await this.secureStorage.getItem('currentUser', true);
      if (currentUser && !this._isLoaded) {
        this.reload();
      }
    } catch (error) {
      // If loading fails, user is not logged in
    }
  }

  private handleSubscriptionError(error: any): void {
  }

  load(): Observable<SubscriptionInfo> {
   if (this._isLoaded && this._info$.value) {
      return of(this._info$.value);
    }

    const url = `${this.BASE_URL}/${this.API_VERSION}/subscription/me`;
    return this.http.get<SubscriptionInfo>(url).pipe(
      tap(info => {
        this._info$.next(info);
        this._isLoaded = true;
      }),
      catchError(error => {
        this.handleSubscriptionError(error);
        return of(null);
      })
    );
  }

  reload(): void {
    const url = `${this.BASE_URL}/${this.API_VERSION}/subscription/me`;
    this.http.get<SubscriptionInfo>(url).subscribe({
      next: (info) => {
        this._info$.next(info);
        this._isLoaded = true;
      },
      error: (error) => {
        this.handleSubscriptionError(error);
      }
    });
  }

  clear(): void {
    this._info$.next(null);
    this._isLoaded = false;
  }

  get info(): SubscriptionInfo | null {
    return this._info$.value;
  }

  get isLoaded(): boolean {
    return this._isLoaded;
  }

  isFeatureEnabled(feature: keyof SubscriptionInfo): boolean {
    const info = this._info$.value;
    if (!info) 
      return false;
    if(info.internal)
      return true;
    return !!info && !!info[feature] && !info?.expired;
  }

  hasRemainingCredit(type: 'email' | 'contact' | 'ai'): boolean {
    const info = this._info$.value;
    if (!info) 
      return false;
    if(info.internal)
      return true;
    if(info?.expired)
      return false;
    
    switch (type) {
      case 'email': return info.availableEmailCredits > 0;
      case 'contact': return info.availableContactCredits > 0;
      case 'ai': return info.availableAgentCampaignCredits > 0 && info.availableAgentResearchCredits > 0;
    }
  }

  isNearLimit(type: 'email' | 'contact'): boolean {
    const info = this._info$.value;
    if (!info) 
      return false;
    if(info.internal)
      return false;
    const ratio = type === 'email'
      ? (info.maxEmails - info.availableEmailCredits) / info.maxEmails
      : (info.maxContacts - info.availableContactCredits) / info.maxContacts;
    return ratio >= 0.9;
  }

  triggerPaymentSuccess() {
    this.paymentSuccessSubject.next(true);
  }

  triggerBoosterPackPaymentSuccess() {
    this.boosterPackPaymentSuccessSubject.next(true);
  }

  triggerPaymentFailure() {
    this.paymentFailureSubject.next(true);
  }

  resetPaymentSuccess() {
    this.paymentSuccessSubject.next(false);
  }

  resetBoosterPackPaymentSuccess() {
    this.boosterPackPaymentSuccessSubject.next(false);
  }

  resetPaymentFailure() {
    this.paymentFailureSubject.next(false);
  }
}
